import { useAtom, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo } from "react";
import { createCanvasTemplate, getTemplates, getTemplatesAdmin, getTemplatesTags } from "../api";
import { CanvasTemplateForm, CanvasTemplateMinimal, CanvasTemplateTag } from "shared/datamodel/schemas/canvas-template";
import LoadedState from "../state/loadedState";
import { templateGlobalState } from "state-atoms/templates-atoms";
import { useShowMondayFeatures } from "frontend/canvas-page/hooks/use-show-monday-features";

export function useGetFetchTemplates(isAdmin = false) {
  const setState = useSetAtom(templateGlobalState);

  return useCallback(async () => {
    setState({ loadingState: LoadedState.loading });
    try {
      const [templates, tags] = await Promise.all([isAdmin ? getTemplatesAdmin() : getTemplates(), getTemplatesTags()]);

      const { visibleTags, hiddenTags } = tags.reduce(
        (acc, currentTag) => {
          if (currentTag.isVisible === false && !isAdmin) {
            acc.hiddenTags.push(currentTag);
          } else {
            acc.visibleTags.push(currentTag);
          }
          return acc;
        },
        { visibleTags: [] as CanvasTemplateTag[], hiddenTags: [] as CanvasTemplateTag[] }
      );

      setState({ templates, tags: visibleTags, hiddenTags, loadingState: LoadedState.finished });
    } catch {
      setState({ loadingState: LoadedState.failed });
    }
  }, [setState, isAdmin]);
}

export default function useCanvasTemplates(isAdmin = false) {
  const [state, setState] = useAtom(templateGlobalState);
  const showMondayFeatures = useShowMondayFeatures();

  const fetchTemplatesFunction = useGetFetchTemplates(isAdmin);

  async function fetchTemplates(force: boolean) {
    if (state.loadingState == LoadedState.loading) return; // Prevent multiple concurrent calls
    if (!force && state.loadingState == LoadedState.finished) return; // Prevent multiple calls (unless forced)
    await fetchTemplatesFunction();
  }

  useEffect(() => {
    fetchTemplates(false);
  }, []);
  const forceReload = () => fetchTemplates(true);

  async function createTemplate(template: CanvasTemplateForm) {
    const newTemplate = await createCanvasTemplate(template);
    const newTemplates = state.templates ? [...state.templates, newTemplate] : [newTemplate];
    setState((state) => ({ ...state, templates: newTemplates }));
    return newTemplate;
  }

  function editTemplate(template: CanvasTemplateMinimal) {
    const newTemplates = state.templates?.map((t) => (t.id === template.id ? template : t));
    setState((state) => ({ ...state, templates: newTemplates }));
  }

  function deleteTemplate(templateId: string) {
    const newTemplates = state.templates?.filter((t) => t.id !== templateId);
    setState((state) => ({ ...state, templates: newTemplates }));
  }

  const filteredState = useMemo(() => {
    if (!showMondayFeatures && !isAdmin) {
      return {
        ...state,
        templates: state.templates?.filter((template) => !template.metadata?.mondaySolutionId),
        tags: state.tags?.filter((tag) => !tag.name.toLowerCase().includes("synced")),
      };
    }
    return state;
  }, [showMondayFeatures, isAdmin, state]);

  return { ...filteredState, forceReload, createTemplate, editTemplate, deleteTemplate };
}
